import { useT } from "@/i18n";
import { Button } from "./button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./dialog";

export type FormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  submitLabel: string;
  pending?: boolean;
  /** For a form whose submit would be a no-op — a move that moves nothing, say. */
  submitDisabled?: boolean;
  /** Shown above the actions — for a failure that is not field-specific. */
  error?: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
};

/**
 * The phase-1 detail-editing pattern: all editable fields, one submit, one `PATCH`
 * carrying `version`.
 *
 * One submit means one version check, one conflict window and one coherent burst of
 * audit events — which is why this is the pattern while the board (and per-field
 * click-to-edit) does not yet exist. See the dated amendment in
 * `.claude/docs/ui-patterns.md`.
 *
 * Cancel and Escape discard silently: no write, no toast, no audit event.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  pending = false,
  submitDisabled = false,
  error,
  onSubmit,
  children,
}: FormDialogProps) {
  const t = useT();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          {children}

          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending || submitDisabled}>
              {pending ? t("common.saving") : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
